<template>
	<!--
	  Custom select controls like this require a considerable amount of JS to implement from scratch. We're planning
	  to build some low-level libraries to make this easier with popular frameworks like React, Vue, and even Alpine.js
	  in the near future, but in the mean time we recommend these reference guides when building your implementation:

	  https://www.w3.org/TR/wai-aria-practices/#Listbox
	  https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
	-->
	<div class="select-dropdown-container space-y-1" :class="[{'open': isOpen, 'disabled': disabled}, additionalClass]">
	  <label id="listbox-label" class="block text-sm leading-5 font-medium text-gray-700" v-if="title">
	    {{ title }}
	  </label>
	  <div class="relative">
	    <span class="select-dropdown-button-container">
	      <button type="button" aria-haspopup="listbox" aria-expanded="true" aria-labelledby="listbox-label" @click="toggle" @blur="blur" :class="{'error-highlight': error}">
	        <span class="block truncate text-base" v-if="items.length > 0">
	          {{ (selected) ? selected.name : '' }}
	        </span>
	        <span v-else>
	        	Loading...
	        </span>
	        <span class="select-dropdown-button-icon">
	          <div class="icon"  v-if="additionalClass == 'quote-input-select'">
	          	<i class="fas fa-arrow-right"></i>
	          </div>
	          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" v-else>
	            <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
	          </svg>
	        </span>
	      </button>
	    </span>

	    <!-- Select popover, show/hide based on select state. -->
	    <div class="select-dropdown-elements" @mouseover="mouseEnter" @mouseleave="mouseLeave">
	      <ul class="dropdown-list" tabindex="-1" role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-item-3">
	        <!--
	          Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

	          Highlighted: "text-white bg-indigo-600", Not Highlighted: "text-gray-900"
	        -->
	        <li id="listbox-item-0" role="option" v-for="item in allItems" @click="selectItem(item)">
	          <!-- Selected: "font-semibold", Not Selected: "font-normal" -->
	          <span class="select-dropdown-element-text">
				<slot name='title' :item='item'>
					{{ item.name }}
				</slot>
	          </span>

	          <!--
	            Checkmark, only display for selected option.

	            Highlighted: "text-white", Not Highlighted: "text-indigo-600"
	          -->
	          <span class="select-dropdown-element-icon" v-if="(item.id && item.id == selected.id) || (item.name && item.name == selected.name)">
	            <svg viewBox="0 0 20 20" fill="currentColor">
	              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
	            </svg>
	          </span>
	        </li>

	        <!-- More options... -->
	      </ul>
	    </div>
	  </div>
	</div>
</template>

<script>
	export default {
		name: 'dropdown',
		props: {
			items: {
				required: true,
			},
			title: {
				type: String
			},
			additionalClass: {
				type: String,
				required: false,
			},
			returnElements: {
				required: false
			},
			disabled: {
				type: Boolean,
				default: false,
			},
			placeholder: {
				type: Boolean,
				default: false,
			},
			val: {},
			error: {
				type: Boolean,
				default: false,
			}
		},
		computed: {
			allItems() {
				let itemsArr = [];

				// add placeholder item to dropdown
				if(this.placeholder) {
					itemsArr.push({
						id: 'not-selected-el',
						name: 'Not Selected',
						value: 0,
					})
				}

				itemsArr = itemsArr.concat(this.items);

				return itemsArr;
			}
		},
		data() {
			return {
				isOpen: false,
				selected: false,
				overDropdown: false,
			}
		},
		mounted() {
			this.selected = this.allItems[0];

			if(this.val) {
				this.selected = this.val;
			}
		},
		watch: {
			allItems: function (newQuote, oldQuote) {
				if(!this.val) {
					this.selected = this.allItems[0];
				}
			},
			val: function(newVal, oldVal) {
				this.selected = this.val;
			}
		},
		methods: {
			toggle() {
				// toggle shouldnt work if disabled
				if(!this.disabled) {
					if(this.items.length > 0) {
						if(this.isOpen) {
							this.isOpen = false;
						} else {
							this.isOpen = true;
						}
					}
				}
			},
			selectItem(item) {
				if(this.returnElements) {
					Object.keys(this.returnElements).forEach((el) => {
						item[el] = this.returnElements[el];
					});
				}

				this.selected = item;

				this.toggle();

				this.$emit('updated', item);
			},
			blur() {
				if(!this.overDropdown) {
					this.isOpen = false;
				}
			},
			mouseEnter() {
				this.overDropdown = true;
			},
			mouseLeave() {
				this.overDropdown = false;
			}
		}
	}
</script>
